import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchPageBySlug, getResizedImageUrl } from '../lib/cms';
import { injectInternalLinks } from '../lib/utils';

export default function Ydelser() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadPage() {
      const data = await fetchPageBySlug('ydelser');
      setPageData(data);
      setLoading(false);
    }
    loadPage();
  }, []);

  useEffect(() => {
    if (pageData) {
      document.title = pageData.meta_title || 'Ydelser - Larsen & Kristensen';
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', pageData.meta_description || '');
      }
    }
  }, [pageData]);

  const services = [
    {
      title: 'Belægning',
      slug: 'belaegning',
      path: '/ydelser/belaegning',
      icon: 'fa-solid fa-border-all',
      desc: 'Professionel flisebelægning, indkørsler, terrasser og støttemure i højeste kvalitet.',
      image: 'https://assets.auxodev.dk/assets/54439585-0ea9-462f-a6c1-e69db40e10d7/danish-house-modern-landscaped-garden-driveway.jpg'
    },
    {
      title: 'Entreprenør',
      slug: 'entreprenoer',
      path: '/ydelser/entreprenoer',
      icon: 'fa-solid fa-helmet-safety',
      desc: 'Jord-, grave- og entreprenørarbejde udført effektivt med en moderne maskinpark.',
      image: 'https://assets.auxodev.dk/assets/54439585-0ea9-462f-a6c1-e69db40e10d7/danish-excavator-construction-site.jpg'
    },
    {
      title: 'Støbearbejde',
      slug: 'stoebearbejde',
      path: '/ydelser/stoebearbejde',
      icon: 'fa-solid fa-layer-group',
      desc: 'Sikre og stabile fundamenter støbt efter alle gældende krav og standarder.',
      image: 'https://assets.auxodev.dk/assets/54439585-0ea9-462f-a6c1-e69db40e10d7/danish-patio-stone-retaining-wall.jpg'
    },
    {
      title: 'Betonarbejde',
      slug: 'betonarbejde',
      path: '/ydelser/betonarbejde',
      icon: 'fa-solid fa-fill-drip',
      desc: 'Klassisk betonarbejde, støbning af gulve, sokler og slidstærke betonløsninger.',
      image: 'https://assets.auxodev.dk/assets/54439585-0ea9-462f-a6c1-e69db40e10d7/danish-masonry-bricklayer-working.jpg'
    },
    {
      title: 'Murerarbejde',
      slug: 'murerarbejde',
      path: '/ydelser/murerarbejde',
      icon: 'fa-solid fa-trowel-bricks',
      desc: 'Klassisk murerhåndværk, opmuring, flisearbejde og renovering af facader.',
      image: 'https://assets.auxodev.dk/assets/54439585-0ea9-462f-a6c1-e69db40e10d7/danish-masonry-bricklayer-working.jpg'
    },
    {
      title: 'Totalløsninger',
      slug: 'totalloesninger',
      path: '/ydelser/totalloesninger',
      icon: 'fa-solid fa-screwdriver-wrench',
      desc: 'Vi tager ansvaret for hele dit projekt – fra første spadestik til færdigt byggeri.',
      image: 'https://assets.auxodev.dk/assets/54439585-0ea9-462f-a6c1-e69db40e10d7/image00001-min-scaled_4.webp'
    },
    {
      title: 'Anlægsarbejde',
      slug: 'anlaegsarbejde',
      path: '/ydelser/anlaegsarbejde',
      icon: 'fa-solid fa-seedling',
      desc: 'Etablering af haver, rullegræs, plantebede og professionel træfældning.',
      image: 'https://assets.auxodev.dk/assets/54439585-0ea9-462f-a6c1-e69db40e10d7/nyanlaeg-af-have-i-skive_6.jpg'
    },
  ];

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-[#163A2B] border-t-transparent"></div>
      </div>
    );
  }

  const heroBg = pageData?.hero_image?.[0] || 'https://assets.auxodev.dk/assets/54439585-0ea9-462f-a6c1-e69db40e10d7/entreprenoer_10.webp';
  const resizedHeroBg = getResizedImageUrl(heroBg, 1600, 80);

  return (
    <div className="flex flex-col min-h-screen">
      
      {/* Page Header / Hero */}
      <section className="relative bg-[#1C1E1D] py-16 sm:py-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={resizedHeroBg}
            alt="Vores ydelser"
            className="h-full w-full object-cover object-center opacity-50"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#163A2B]/50 via-[#1C1E1D]/70 to-[#1C1E1D]/90"></div>
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#163A2B] bg-[#F4F1EA] px-3 py-1.5 rounded-full">
            Hvad vi tilbyder
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mt-4 font-display">
            {pageData?.title || 'Vores Ydelser'}
          </h1>
          <p className="mt-4 text-base sm:text-lg text-[#F4F1EA]/80 max-w-2xl mx-auto leading-relaxed">
            {pageData?.intro || 'Vi tilbyder et bredt spektrum af professionelle ydelser inden for anlæg, byggeri og entreprenørarbejde.'}
          </p>
        </div>
      </section>

      {/* Intro Text Section */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <div 
            className="text-base sm:text-lg text-[#1C1E1D]/80 leading-relaxed space-y-4 prose prose-emerald mx-auto"
            dangerouslySetInnerHTML={{ __html: injectInternalLinks(pageData?.content_html, 'ydelser') }}
          />
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-12 sm:py-20 bg-[#F4F1EA]/50 border-t border-[#E2E2E0]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-10">
            {services.map((service) => (
              <div 
                key={service.slug}
                className="bg-white border border-[#E2E2E0] rounded-lg overflow-hidden shadow-sm flex flex-col justify-between"
              >
                {/* Image & Icon Overlay */}
                <div className="relative aspect-16/10 overflow-hidden bg-slate-100">
                  <img
                    src={getResizedImageUrl(service.image, 600, 80)}
                    alt={service.title}
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute top-4 left-4 bg-[#163A2B] text-white p-3 rounded-md shadow-md flex items-center justify-center w-10 h-10">
                    <i className={`${service.icon} text-sm`} />
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-6 sm:p-8 flex-1 flex flex-col justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-[#1C1E1D] font-display">
                      {service.title}
                    </h3>
                    <p className="text-sm text-[#1C1E1D]/70 mt-3 leading-relaxed">
                      {service.desc}
                    </p>
                  </div>

                  <div className="mt-8 pt-4 border-t border-[#E2E2E0]">
                    <Link
                      to={service.path}
                      className="inline-flex w-full items-center justify-center rounded-md bg-[#163A2B] px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-[#1C1E1D] transition-colors cursor-pointer"
                    >
                      Læs mere om {service.title.toLowerCase()} <i className="fa-solid fa-arrow-right ml-2 text-xs" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Trust Banner */}
      <section className="bg-[#1C1E1D] text-white py-16 border-t border-[#E2E2E0]">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold font-display">
            Brug for en skræddersyet løsning?
          </h2>
          <p className="text-sm sm:text-base text-[#F4F1EA]/80 mt-3 max-w-2xl mx-auto leading-relaxed">
            Mange af vores opgaver er unikke og kræver individuel planlægning. Vi rådgiver dig gerne om materialevalg, konstruktioner og tidsplaner, så du får det bedst mulige resultat.
          </p>
          <div className="mt-8">
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center rounded-md bg-[#163A2B] px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-white hover:text-[#1C1E1D] transition-colors cursor-pointer"
            >
              <i className="fa-solid fa-phone mr-2 text-xs" /> Kontakt os for en uforpligtende snak
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
}
